import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  TextField,
  InputAdornment,
} from '@mui/material';
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { enGB, pl as plPL } from 'date-fns/locale';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from '@mui/material/styles';
import { getGearLabel } from '../gears';
import { getCategoryLabel } from '../categories';
import CityFilter from './CityFilter';

/** Height for date, duration, category, gear (compact row) */
const COMPACT_H = 34;
/** Space name + SEARCH button — aligned with each other, slightly taller than compact controls */
const PRIMARY_ROW_H = 40;
const COMPACT_PAD = '6px 11px';
const COMPACT_FS = { xs: '0.74rem', sm: '0.78rem' } as const;
const PRIMARY_FS = { xs: '0.78rem', sm: '0.82rem' } as const;
const COMPACT_LABEL_FS = { xs: '0.68rem', sm: '0.72rem' } as const;
const PRIMARY_LABEL_FS = { xs: '0.7rem', sm: '0.74rem' } as const;

interface SearchBarProps {
  onSearch?: (searchParams: {
    date: Date | null;
    duration: number;
    gears: string[];
    categories: string[];
    spaceName: string;
  }) => void;
  onFilterClick?: () => void;
  disabled?: boolean;
  initialDate?: Date | null;
  initialDuration?: number;
  initialGears?: string[];
  initialCategories?: string[];
  initialSpaceName?: string;
  rightActions?: React.ReactNode;
}

const SearchBar: React.FC<SearchBarProps> = ({
  onSearch,
  onFilterClick: _onFilterClick,
  disabled = false,
  initialDate,
  initialDuration = 120,
  initialGears = [],
  initialCategories = [],
  initialSpaceName = '',
  rightActions,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  /** Toolbar stacks on two rows below lg so Search + toggles never overlap filters (sidebar narrows content). */
  const isLgUp = useMediaQuery(theme.breakpoints.up('lg'));
  const maxTagsToShow = isMobile ? 2 : 2; // Show max 2 tags to prevent overflow
  
  // Initialize with current time + 2 hours if no initial date
  const defaultDate = initialDate || (() => {
    const now = new Date();
    const minutes = Math.ceil(now.getMinutes() / 30) * 30;
    now.setMinutes(minutes);
    now.setSeconds(0);
    now.setMilliseconds(0);
    now.setHours(now.getHours() + 2);
    return now;
  })();

  const [selectedDate, setSelectedDate] = useState<Date | null>(defaultDate);
  const [duration, setDuration] = useState<number>(initialDuration);
  const [selectedGears, setSelectedGears] = useState<string[]>(initialGears);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(initialCategories);
  const [spaceName, setSpaceName] = useState<string>(initialSpaceName);

  useEffect(() => {
    setSpaceName(initialSpaceName);
  }, [initialSpaceName]);

  // Duration options: 1h to 12h inclusive, in 30-minute increments
  const durationOptions = Array.from({ length: 23 }, (_, i) => 60 + i * 30);

  // Available gear types
  const availableGearTypes = [
    'GUITAR_AMP', 'BASS_AMP', 'DRUM_KIT', 'MICROPHONE', 'KEYBOARD', 
    'GUITAR', 'BASS_GUITAR', 'MIXER', 'SPEAKERS', 'VIOLIN'
  ];

  // Available room categories
  const availableCategories = ['REHEARSAL', 'RECORDING', 'PRACTICE'];

  const handleSearch = () => {
    if (onSearch && selectedDate) {
      onSearch({
        date: selectedDate,
        duration,
        gears: selectedGears,
        categories: selectedCategories,
        spaceName,
      });
    }
  };


  return (
    <LocalizationProvider 
      dateAdapter={AdapterDateFns} 
      adapterLocale={((): Locale => {
        const lang = t('calendar.locale', 'en');
        if (lang === 'pl') return plPL;
        return enGB;
      })()}
    >
      <Paper
        elevation={0}
        sx={{
          display: 'flex',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 1, sm: 1 },
          p: { xs: 1, sm: 1 },
          borderRadius: 0,
          backgroundColor: '#1F2129',
          border: 'none',
          width: '100%',
          minWidth: 0,
          flexDirection: { xs: 'column', sm: 'row' },
          flexWrap: { xs: 'nowrap', sm: isLgUp ? 'nowrap' : 'wrap' },
          rowGap: { sm: 1 },
          overflow: 'visible'
        }}
      >

        {/* Mobile City Filter */}
        <Box sx={{ display: { xs: 'block', sm: 'none' }, mb: 1 }}>
          <CityFilter />
        </Box>

        {/* Inputs container: 2 columns on mobile (date+duration on row 1, gear spans row 2),
            row layout on desktop. Use minmax to allow shrinking on very narrow screens. */}
        <Box sx={{
          display: { xs: 'grid', sm: 'flex' },
          gridTemplateColumns: { xs: 'minmax(0,1fr) minmax(0,1fr)', sm: 'none' },
          gap: 1,
          alignItems: { sm: 'center' },
          flex: { sm: 1 },
          flexBasis: { sm: isLgUp ? 0 : '100%' },
          minWidth: 0,
          maxWidth: { sm: isLgUp ? 'none' : '100%' },
          // NOTE: overflow-x: auto would force overflow-y to clip and cut off outlined InputLabels — keep visible.
          overflow: 'visible',
          flexWrap: { sm: 'nowrap' },
          pt: { xs: '6px', sm: '4px' },
        }}>
          {/* Space name — same vertical band as SEARCH (PRIMARY_ROW_H), no extra top padding */}
          <Box sx={{
            width: '100%',
            minWidth: { xs: 0, sm: '140px' },
            maxWidth: { xs: '100%', sm: 'none' },
            flex: { sm: '1 1 0' },
            flexShrink: { sm: 1 },
            flexGrow: { sm: 1 },
            gridColumn: { xs: '1 / -1', sm: 'auto' },
            overflow: 'visible',
            alignSelf: { sm: 'center' },
            background: 'transparent',
            border: 'none',
          }}>
            <TextField
              size="small"
              fullWidth
              value={spaceName}
              onChange={(e) => setSpaceName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSearch();
                }
              }}
              disabled={disabled}
              label={t('search.spaceName', 'Space name')}
              placeholder={t('search.spaceNamePlaceholder', 'Search by space name')}
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0, ml: 0.15 }}>
                    <SearchIcon sx={{ color: 'primary.main', fontSize: 19, opacity: 0.7 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                overflow: 'visible',
                '& .MuiInputBase-root': {
                  minHeight: PRIMARY_ROW_H,
                  maxHeight: PRIMARY_ROW_H,
                  minWidth: 0,
                  maxWidth: '100%',
                },
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'transparent',
                  overflow: 'visible',
                  '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.2)' },
                  '&:hover fieldset': { borderColor: 'rgba(79, 95, 235, 0.45)' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1.5 },
                },
                '& .MuiInputBase-input': {
                  py: '8px',
                  px: '10px',
                  pl: 0.25,
                  pr: '11px',
                  fontSize: PRIMARY_FS,
                  fontWeight: 600,
                  letterSpacing: '0.01em',
                  color: 'rgba(255,255,255,0.88)',
                  minWidth: 0,
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255,255,255,0.65)',
                  fontWeight: 600,
                  '&.Mui-focused': { color: 'primary.main' },
                  fontSize: PRIMARY_LABEL_FS,
                },
                '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                  transform: 'translate(32px, -11px) scale(0.72)',
                },
              }}
            />
          </Box>

          {/* Date & Time Picker (compact) */}
          <Box sx={{ 
            width: '100%', 
            minWidth: { xs: 0, sm: '148px' }, 
            maxWidth: { xs: '100%', sm: 'min(220px, 32vw)' },
            flex: { sm: '0 1 auto' },
            flexShrink: { sm: 1 },
            flexGrow: { sm: 0 }
          }}>
            <DateTimePicker
              value={selectedDate}
              onChange={setSelectedDate}
              minutesStep={30}
              disabled={disabled}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  label: t('search.dateTime', 'Date & Time'),
                  InputLabelProps: { shrink: true },
                  sx: {
                    width: '100%',
                    minWidth: 0,
                    maxWidth: '100%',
                    overflow: 'visible',
                    '& .MuiInputBase-root': {
                      minHeight: COMPACT_H,
                      minWidth: 0,
                      maxWidth: '100%',
                    },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1.5,
                      backgroundColor: 'rgba(255, 255, 255, 0.08)',
                      overflow: 'visible',
                      '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.18)' },
                      '&:hover fieldset': { borderColor: 'primary.main' },
                      '&.Mui-focused fieldset': { borderColor: 'primary.main', borderWidth: 1.5 },
                    },
                    '& .MuiInputBase-input': { 
                      py: '6px',
                      px: '11px',
                      fontSize: COMPACT_FS, 
                      color: 'rgba(255,255,255,0.88)',
                      minWidth: 0,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis'
                    },
                    '& .MuiInputLabel-root': { 
                      color: 'rgba(255,255,255,0.65)', 
                      '&.Mui-focused': { color: 'primary.main' },
                      fontSize: COMPACT_LABEL_FS
                    },
                    '& .MuiInputLabel-root.MuiInputLabel-shrink': {
                      transform: 'translate(11px, -10.5px) scale(0.72)',
                    },
                    '& .MuiIconButton-root': {
                      p: { xs: 0.35, sm: 0.5 }
                    }
                  },
                },
              }}
            />
          </Box>

          {/* Duration Picker (compact) */}
          <Box sx={{ 
            width: '100%', 
            minWidth: { xs: 0, sm: '92px' }, 
            maxWidth: { xs: '100%', sm: '120px' },
            flex: { sm: '0 0 auto' },
            flexShrink: { sm: 0 },
            flexGrow: { sm: 0 }
          }}>
            {/* No InputLabel: with a selected value MUI would shrink the label into the notch and
                add vertical space — Category/Gears stay one row because the label sits inside when empty. */}
            <FormControl size="small" fullWidth sx={{
              width: '100%',
              '& .MuiInputBase-root': { minHeight: COMPACT_H, maxHeight: COMPACT_H },
            }}>
              <Select
                value={duration}
                onChange={(e) => setDuration(Number(e.target.value))}
                disabled={disabled}
                displayEmpty
                inputProps={{ 'aria-label': t('search.duration', 'Duration') }}
                renderValue={(mins) => {
                  const m = mins as number;
                  const hrs = Math.floor(m / 60);
                  const rem = m % 60;
                  const parts = [hrs > 0 ? `${hrs}h` : null, rem > 0 ? `${rem}m` : null].filter(Boolean);
                  return <span style={{ color: 'rgba(255,255,255,0.88)', fontSize: 'inherit' }}>{parts.join(' ')}</span>;
                }}
                sx={{
                  width: '100%',
                  minWidth: 'unset',
                  maxWidth: '100%',
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  '& .MuiInputBase-root': { 
                    height: COMPACT_H,
                    minHeight: COMPACT_H,
                    width: '100%',
                    minWidth: 'unset',
                    maxWidth: '100%'
                  },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.18)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main', borderWidth: 1.5 },
                  '& .MuiSelect-select': { 
                    color: 'rgba(255, 255, 255, 0.88)',
                    fontSize: COMPACT_FS,
                    height: COMPACT_H,
                    minHeight: COMPACT_H,
                    p: COMPACT_PAD,
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    minWidth: 'unset',
                    maxWidth: '100%'
                  },
                }}
              >
                {durationOptions.map((mins) => {
                  const hrs = Math.floor(mins / 60); const rem = mins % 60;
                  const parts = [hrs > 0 ? `${hrs}h` : null, rem > 0 ? `${rem}m` : null].filter(Boolean);
                  return <MenuItem key={mins} value={mins}>{parts.join(' ')}</MenuItem>;
                })}
              </Select>
            </FormControl>
          </Box>

          {/* Category Picker (compact) */}
          <Box sx={{ 
            width: { xs: 'auto', sm: 'auto' }, 
            minWidth: { xs: 0, sm: '108px' }, 
            maxWidth: { xs: '100%', sm: 'min(180px, 24vw)' },
            flex: { xs: '1 1 0', sm: '0 0 auto' }, 
            flexShrink: { xs: 1, sm: 0 },
            flexGrow: { xs: 1, sm: 0 }
          }}>
            <FormControl size="small" fullWidth sx={{
              minWidth: { sm: 108 },
              '& .MuiInputLabel-root': {
                fontSize: COMPACT_LABEL_FS,
                color: 'rgba(255,255,255,0.65)',
                '&.Mui-focused': { color: 'primary.main' },
              },
            }}>
              <InputLabel>{t('search.categories', 'Category')}</InputLabel>
              <Select multiple value={selectedCategories}
                onChange={(e) => {
                  const value = e.target.value as unknown as string[] | string;
                  const next = typeof value === 'string' ? value.split(',') : value;
                  if (Array.isArray(next) && next.includes('__CLEAR__')) {
                    setSelectedCategories([]);
                    return;
                  }
                  setSelectedCategories(next as string[]);
                }}
                label={t('search.categories', 'Category')} disabled={disabled}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', width: '100%', maxWidth: '100%', height: '100%' }}>
                    {selected.length === 0 ? (
                      <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)' }}>{t('search.categories', 'Category')}</span>
                    ) : (
                      <>
                        {(selected as string[]).slice(0, 2).map((category) => (
                          <Box key={category} sx={{ display: 'inline-flex', alignItems: 'center', backgroundColor: 'rgba(79, 95, 235, 0.2)', color: '#4E67EB', px: 0.6, py: 0.15, borderRadius: 0.75, fontSize: '0.65rem', fontWeight: 500, border: '1px solid rgba(79, 95, 235, 0.3)', flexShrink: 0 }}>{getCategoryLabel(category)}</Box>
                        ))}
                        {(selected as string[]).length > 2 && (
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)', px: 0.6, py: 0.15, borderRadius: 0.75, fontSize: '0.65rem', fontWeight: 500, border: '1px solid rgba(255,255,255,0.2)', flexShrink: 0 }}>+{(selected as string[]).length - 2}</Box>
                        )}
                      </>
                    )}
                  </Box>
                )}
                sx={{
                  minWidth: 0,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  height: COMPACT_H,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.18)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main', borderWidth: 1.5 },
                  '& .MuiSelect-select': { 
                    color: 'rgba(255, 255, 255, 0.88)', 
                    display: 'flex', 
                    alignItems: 'center',
                    height: `${COMPACT_H}px !important`,
                    padding: COMPACT_PAD,
                    minHeight: 'unset !important',
                    fontSize: COMPACT_FS,
                  },
                }}
              >
                <MenuItem value="__CLEAR__" sx={{ color: 'error.light' }}>
                  {t('search.clear', 'Clear')}
                </MenuItem>
                {availableCategories.map((category) => (
                  <MenuItem key={category} value={category}>{getCategoryLabel(category)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Gear Picker (compact) */}
          <Box sx={{ 
            width: { xs: 'auto', sm: 'auto' }, 
            minWidth: { xs: 0, sm: '108px' }, 
            maxWidth: { xs: '100%', sm: 'min(180px, 24vw)' },
            flex: { xs: '1 1 0', sm: '0 0 auto' }, 
            flexShrink: { xs: 1, sm: 0 },
            flexGrow: { xs: 1, sm: 0 }
          }}>
            <FormControl size="small" fullWidth sx={{
              minWidth: { sm: 108 },
              '& .MuiInputLabel-root': {
                fontSize: COMPACT_LABEL_FS,
                color: 'rgba(255,255,255,0.65)',
                '&.Mui-focused': { color: 'primary.main' },
              },
            }}>
              <InputLabel>{t('search.gears', 'Gear')}</InputLabel>
              <Select multiple value={selectedGears}
                onChange={(e) => {
                  const value = e.target.value as unknown as string[] | string;
                  const next = typeof value === 'string' ? value.split(',') : value;
                  if (Array.isArray(next) && next.includes('__CLEAR__')) {
                    setSelectedGears([]);
                    return;
                  }
                  setSelectedGears(next as string[]);
                }}
                label={t('search.gears', 'Gear')} disabled={disabled}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden', width: '100%', maxWidth: '100%', height: '100%' }}>
                    {selected.length === 0 ? (
                      <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)' }}>{t('search.gears', 'Gear')}</span>
                    ) : (
                      isMobile ? (
                        <span style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.82)' }}>
                          {t('search.gearsSelected', { count: (selected as string[]).length })}
                        </span>
                      ) : (
                        <>
                          {(selected as string[]).slice(0, maxTagsToShow).map((gear) => (
                            <Box key={gear} sx={{ display: 'inline-flex', alignItems: 'center', backgroundColor: 'rgba(78,103,235,0.2)', color: 'primary.main', px: 0.6, py: 0.15, borderRadius: 0.75, fontSize: '0.65rem', fontWeight: 500, border: '1px solid rgba(78,103,235,0.3)', flexShrink: 0 }}>{getGearLabel(gear)}</Box>
                          ))}
                          {(selected as string[]).length > maxTagsToShow && (
                            <Box sx={{ display: 'inline-flex', alignItems: 'center', backgroundColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)', px: 0.6, py: 0.15, borderRadius: 0.75, fontSize: '0.65rem', fontWeight: 500, border: '1px solid rgba(255,255,255,0.2)', flexShrink: 0 }}>+{(selected as string[]).length - maxTagsToShow}</Box>
                          )}
                        </>
                      )
                    )}
                  </Box>
                )}
                sx={{
                  minWidth: 0,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  height: COMPACT_H,
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.18)' },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main' },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'primary.main', borderWidth: 1.5 },
                  '& .MuiSelect-select': { 
                    color: 'rgba(255, 255, 255, 0.88)', 
                    display: 'flex', 
                    alignItems: 'center',
                    height: `${COMPACT_H}px !important`,
                    padding: COMPACT_PAD,
                    minHeight: 'unset !important',
                    fontSize: COMPACT_FS,
                  },
                }}
              >
                <MenuItem value="__CLEAR__" sx={{ color: 'error.light' }}>
                  {t('search.clear', 'Clear')}
                </MenuItem>
                {availableGearTypes.map((gearType) => (
                  <MenuItem key={gearType} value={gearType}>{getGearLabel(gearType)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Search Button and Right Actions Row on mobile, inline on desktop */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'row',
          flexWrap: 'nowrap',
          gap: 1,
          mt: { xs: 1, sm: isLgUp ? 0 : 0.25 }, 
          ml: { xs: 0, sm: isLgUp ? 1 : 0 },
          width: { xs: '100%', sm: isLgUp ? 'auto' : '100%' },
          flexBasis: { sm: isLgUp ? 'auto' : '100%' },
          justifyContent: { xs: 'stretch', sm: isLgUp ? 'flex-start' : 'flex-end' },
          alignItems: 'center',
          flexShrink: 0,
        }}>
          <Button variant="contained" onClick={handleSearch} disabled={disabled || !selectedDate}
            sx={{
              flex: { xs: 1, sm: 0 },
              minWidth: { xs: 'auto', sm: 96 },
              minHeight: PRIMARY_ROW_H,
              height: PRIMARY_ROW_H,
              py: 0,
              px: 1.5,
              fontSize: PRIMARY_FS,
              fontWeight: 700,
              letterSpacing: '0.04em',
              lineHeight: 1,
              borderRadius: 2,
            }}>
            {t('search.searchButton')}
          </Button>
          {rightActions && (
            <Box sx={{ display: { xs: 'flex', md: 'none' }, flexShrink: 0 }}>
              {rightActions}
            </Box>
          )}
        </Box>
      </Paper>
    </LocalizationProvider>
  );
};

export default SearchBar;
